import { Icon, Input, Typography } from '@smartgift/frontend-toolkit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { MAX_FEATURE_NAME_LENGTH } from '@hero/constants';
import { ROUTE_PATHS } from '@hero/routes';
import { useUpdateCollection } from '@hero/services';

const InputLabel = styled.label`
  background-color: var(--color-accent-secondary-weakest);
  color: var(--color-accent-secondary-default);
  font-size: 1.2rem;
  line-height: 100%;
  min-width: max-content;
  padding: 0 8px;
  position: absolute;
  top: -7px;
  transform: translateX(-9px);
`;

const EditableArea = styled.div`
  align-items: center;
  border: 1px solid transparent;
  cursor: pointer;
  display: flex;
  gap: 8px;
  height: 100%;
  min-width: max-content;
  padding: 8px;
  padding-left: 16px;
  position: absolute;
  width: 100%;

  &:hover {
    border: 1px solid var(--color-accent-secondary-weaker);
    border-radius: var(--size-border-radius-md);
  }
`;

const ActionButton = styled.button`
  align-items: center;
  background: var(--color-bg-body);
  border: 1px solid var(--color-accent-secondary-weaker);
  border-radius: var(--size-border-radius-md);
  box-shadow: var(--shadow-s);
  cursor: ${({ inactive }) => (inactive ? `not-allowed` : `pointer`)};
  display: flex;
  height: 32px;
  justify-content: center;
  margin-left: 3px;
  width: 32px;

  svg path {
    fill: ${({ inactive }) => (inactive ? `var(--color-accent-secondary-default)` : '')};
  }
`;

const ActionButtons = styled.div`
  bottom: -15px;
  display: flex;
  position: absolute;
  right: -8px;
`;

const DetailItem = styled.div`
  min-width: fit-content;
  position: absolute;
  width: 100%;
`;

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 40px;
  min-width: fit-content;
  position: relative;
  width: 100%;

  ${Input.S.Base} {
    width: 100%;
  }
`;

function useCollectionsCreationHeader({ name, setIsPageLoading = () => {} }) {
  const { t } = useTranslation();
  const { collectionId } = useParams();
  const navigate = useNavigate();

  const { mutateAsync: updateCollection } = useUpdateCollection();

  const [edit, setEdit] = useState(false);
  const [collectionName, setCollectionName] = useState(name);
  const [inputRef, setInputRef] = useState(null);

  async function updateCollectionWithLoading() {
    setIsPageLoading(true);
    await updateCollection({
      params: { collectionId },
      body: {
        name: collectionName
      }
    });
    setIsPageLoading(false);
  }

  useEffect(() => {
    inputRef?.focus();
  }, [inputRef]);

  useEffect(() => {
    if (name) {
      setCollectionName(name);
    }
  }, [name]);

  return {
    title: (
      <TitleWrapper>
        {!edit ? (
          <EditableArea
            onClick={() => {
              setEdit(true);
            }}
          >
            <InputLabel>{t('collectionName')}</InputLabel>
            <Typography
              variant="body3"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 'max-content'
              }}
            >
              {name}
            </Typography>
            <Icon name="edit" />
          </EditableArea>
        ) : (
          <DetailItem>
            <Input
              ref={setInputRef}
              label={`${t('collectionName')}*`}
              value={collectionName}
              variant="outlined"
              onChange={e => setCollectionName(e.target.value)}
              error={!collectionName}
              inputProps={{ maxLength: MAX_FEATURE_NAME_LENGTH }}
            />
            <ActionButtons>
              <ActionButton
                onClick={() => {
                  setEdit(false);
                  setCollectionName(name);
                }}
              >
                <Icon name="close" />
              </ActionButton>
              <ActionButton
                disabled={!collectionName}
                onClick={async () => {
                  if (name !== collectionName) {
                    await updateCollectionWithLoading();
                  }
                  setEdit(false);
                }}
              >
                <Icon name="success" />
              </ActionButton>
            </ActionButtons>
          </DetailItem>
        )}
      </TitleWrapper>
    ),
    endButton: {
      children: t('saveAndClose'),
      onClick: async () => {
        navigate(ROUTE_PATHS.COLLECTIONS());
      }
    },
    showBorder: true,
    style: {
      backgroundColor: 'var(--color-bg-weakest2)'
    }
  };
}

export { useCollectionsCreationHeader };
